import { cn } from "@/utils/helpers";
import { TurnDot } from "../components/DateRail";
import type { ScopeLine } from "./calendarModel";

/**
 * CalendarScope (DESIGN.md "The Calendar in Parts"): the month's scope along
 * the foot, a bucket at a time, with the whose-turn key on the right. A bucket
 * off its target says by how much in alert ink. Pressing the scope opens the
 * month's cadence editor.
 */
export default function CalendarScope({
  monthName,
  lines,
  counting = false,
  otherName,
  showKey = true,
  onEdit,
}: {
  monthName: string;
  lines: ScopeLine[] | null;
  /** The month's posts are still loading. */
  counting?: boolean;
  /** Whose other turn the key names: the client's for the team, the team's for the client. */
  otherName: string;
  showKey?: boolean;
  onEdit?: () => void;
}) {
  const body = (
    <>
      <span className="text-[13px] font-semibold text-co-text">{monthName} scope</span>
      {counting ? (
        <span className="text-[13px] text-co-text2">Counting…</span>
      ) : lines ? (
        lines.map((line) => (
          <span
            key={line.key}
            className="text-[13px] tabular-nums"
            style={{ color: line.mismatch ? "var(--co-alert-ink)" : "var(--co-text2)" }}
          >
            {line.text}
          </span>
        ))
      ) : (
        <span className="text-[13px] text-co-text2">No scope set</span>
      )}
    </>
  );
  return (
    <div data-cal-scope="" className="sticky bottom-0 z-20 flex min-h-11 items-center justify-between gap-[18px] border-t border-co-line bg-co-ground px-8 max-lg:-mx-3">
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          aria-label={`${monthName} scope. Change it`}
          className={cn(
            "flex min-w-0 flex-wrap items-center gap-x-[18px] gap-y-1 pb-3.5 pt-3 text-left leading-[18px] outline-none",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
            "hover:[&>span:first-child]:underline",
          )}
        >
          {body}
        </button>
      ) : (
        <div className="flex min-w-0 flex-wrap items-center gap-x-[18px] gap-y-1 pb-3.5 pt-3 leading-[18px]">{body}</div>
      )}
      {showKey ? (
        <span className="flex shrink-0 items-center gap-[18px] text-[12px] text-co-text2">
          <span className="flex items-center gap-1.5">
            {/* The key's own dot is the accent itself (Blue on light), not its ink. */}
            <span aria-hidden="true" className="size-[7px] shrink-0 rounded-full bg-co-accent" />
            Your turn
          </span>
          <span className="flex items-center gap-1.5">
            <TurnDot owner="client" />
            {otherName}&rsquo;s turn
          </span>
        </span>
      ) : null}
    </div>
  );
}
